import { DragEndEvent } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTrackOrderUpdateMutation } from "../api/releases/hooks/useTrackOrderUpdateMutation";
import { QUERY_KEYS } from "../constants/queryKeys";
import { usePlayPauseOnFooter } from "./audioPlayerHooks/useGetFooterPlayerRef";
import { useAppSelector } from "../store/hooks";
import { ReleaseTrack } from "../store/models/release";
import { arrayMove } from "@dnd-kit/sortable";

interface UseReleaseTrackDragDropProps {
  /**
   * The release tracks to be reordered
   */
  releaseTracks: ReleaseTrack[];
}

/**
 * A hook that handles drag and drop functionality for release tracks
 */
export const useReleaseTrackDragDrop = ({
  releaseTracks,
}: UseReleaseTrackDragDropProps) => {
  const queryClient = useQueryClient();
  const { isFooterPlaying } = useAppSelector((state) => state.abPlayerStore);
  const { handleClick: pauseFooterPlayer } = usePlayPauseOnFooter();
  const { mutateAsync: updateTrackOrder, isPending } =
    useTrackOrderUpdateMutation();

  const handleDragStart = useCallback(() => {
    if (isFooterPlaying) {
      pauseFooterPlayer();
    }
  }, [isFooterPlaying, pauseFooterPlayer]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over?.id) {
        return;
      }

      const oldIndex = releaseTracks.findIndex(
        (track) => track.id === active.id,
      );
      const newIndex = releaseTracks.findIndex((track) => track.id === over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return;
      }

      const updatedOrder = arrayMove(releaseTracks, oldIndex, newIndex);

      queryClient.setQueryData(
        [QUERY_KEYS.FETCH_ARTIST_RELEASE_TRACKS, releaseTracks[0].release.id],
        updatedOrder,
      );

      void updateTrackOrder({
        releaseId: releaseTracks[0].release.id,
        updatedTrackOrder: updatedOrder.map((track) => track.id),
      });
    },
    [releaseTracks, queryClient, updateTrackOrder],
  );

  return {
    handleDragStart,
    handleDragEnd,
    isPending,
  };
};
